import { useCallback, useEffect, useState } from "react";
import type { VillageSatelliteImage } from "../../../domain/contracts";
import { downloadVillageSatelliteBundle } from "../../../services/media/satelliteDownloadService";

type UseVillageSatellitePanelActionsArgs = {
  villageName: string | null;
  satelliteItems: VillageSatelliteImage[];
};

export type VillageSatellitePanelActions = {
  isFullscreen: boolean;
  isDownloading: boolean;
  canDownload: boolean;
  toggleFullscreen: () => void;
  closeFullscreen: () => void;
  downloadAll: () => void;
};

export function useVillageSatellitePanelActions({
  villageName,
  satelliteItems,
}: UseVillageSatellitePanelActionsArgs): VillageSatellitePanelActions {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((current) => !current);
  }, []);

  const downloadAll = useCallback(() => {
    if (satelliteItems.length === 0 || isDownloading) {
      return;
    }

    setIsDownloading(true);
    void downloadVillageSatelliteBundle(satelliteItems, villageName)
      .finally(() => {
        setIsDownloading(false);
      });
  }, [isDownloading, satelliteItems, villageName]);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isFullscreen]);

  return {
    isFullscreen,
    isDownloading,
    canDownload: satelliteItems.length > 0 && !isDownloading,
    toggleFullscreen,
    closeFullscreen,
    downloadAll,
  };
}
